import { useNavigate } from 'react-router-dom';

import { appColors, ContentNugget, ContentNuggetBookmark } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { groupNuggetsByCategory } from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { setCurrentDialogNugget } from '@backrs_ui/grow-ui/src/redux/contentNugget/contentNugget';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';

import { Box, Typography, SxProps, Theme } from '@mui/material';

import { mapBookmarksToContentNugget } from './bookmarks.util';
import { ExpandableNuggetGroup } from '../components/ExpandableNuggetGroup';

const BOOKMARK_STRINGS = appStrings.modules;

type BookmarkPageProps = Readonly<{
  completedNuggetIds?: number[];
  bookmarks?: ContentNuggetBookmark[];
}>;

export const BookmarkPage = ({ completedNuggetIds, bookmarks }: BookmarkPageProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const bookmarksAsNuggets: ContentNugget[] = mapBookmarksToContentNugget(bookmarks ?? []);

  const {
    learnAndExploreNuggets,
    realPeopleRealStoriesNuggets,
    growByDoingNuggets,
    scholarshipNuggets,
    internshipNuggets,
    careerPathNuggets,
    profileNuggets,
    postNuggets,
    externalProfileNuggets,
    collegeNuggets,
  } = groupNuggetsByCategory(bookmarksAsNuggets);

  const handleOpeningDialog = (nugget: ContentNugget) => {
    if (nugget.variant === 'institution' && nugget.college_info) {
      navigate(`${APP_ROUTE.UNIVERSITY}/${nugget.college_info.id}`);

      return;
    }

    dispatch(setCurrentDialogNugget(nugget));
  };

  return (
    <>
      <Box sx={parentContainerStyle}>
        <Typography variant="h4" sx={titleStyle}>
          {BOOKMARK_STRINGS.bookmarks.pageHeader}
        </Typography>
        <Box sx={contentContainerStyle}>
          {/* MOST RECENT */}
          {bookmarksAsNuggets.length > 0 && (
            <ExpandableNuggetGroup
              nuggetList={bookmarksAsNuggets}
              handleOpeningDialog={handleOpeningDialog}
              rowHeader={BOOKMARK_STRINGS.bookmarks.mostRecent}
              completedNuggetIds={completedNuggetIds}
            />
          )}

          {/* LEARN & EXPLORE */}
          {learnAndExploreNuggets.length > 0 && (
            <ExpandableNuggetGroup
              nuggetList={learnAndExploreNuggets}
              handleOpeningDialog={handleOpeningDialog}
              rowHeader={BOOKMARK_STRINGS.competency.columnHeaders.learnAndExplore}
            />
          )}

          {/* REAL PEOPLE, REAL STORIES */}
          {realPeopleRealStoriesNuggets.length > 0 && (
            <ExpandableNuggetGroup
              nuggetList={realPeopleRealStoriesNuggets}
              handleOpeningDialog={handleOpeningDialog}
              rowHeader={BOOKMARK_STRINGS.competency.columnHeaders.realPeopleRealStories}
            />
          )}

          {/* GROW BY DOING */}
          {growByDoingNuggets.length > 0 && (
            <ExpandableNuggetGroup
              nuggetList={growByDoingNuggets}
              handleOpeningDialog={handleOpeningDialog}
              rowHeader={BOOKMARK_STRINGS.competency.columnHeaders.growByDoing}
              completedNuggetIds={completedNuggetIds}
            />
          )}

          {/* COLLEGES */}
          {collegeNuggets.length > 0 && (
            <ExpandableNuggetGroup
              nuggetList={collegeNuggets}
              handleOpeningDialog={handleOpeningDialog}
              rowHeader={'Colleges'}
            />
          )}

          {/* SCHOLARSHIPS */}
          {scholarshipNuggets.length > 0 && (
            <ExpandableNuggetGroup
              nuggetList={scholarshipNuggets}
              handleOpeningDialog={handleOpeningDialog}
              rowHeader={'Scholarships'}
            />
          )}

          {/* INTERNSHIPS */}
          {internshipNuggets.length > 0 && (
            <ExpandableNuggetGroup
              nuggetList={internshipNuggets}
              handleOpeningDialog={handleOpeningDialog}
              rowHeader={'Internships'}
            />
          )}

          {/* CAREER PATH */}
          {careerPathNuggets.length > 0 && (
            <ExpandableNuggetGroup
              nuggetList={careerPathNuggets}
              handleOpeningDialog={handleOpeningDialog}
              rowHeader={'Career Paths'}
            />
          )}

          {/* POSTS */}
          {postNuggets.length > 0 && (
            <ExpandableNuggetGroup
              nuggetList={postNuggets}
              handleOpeningDialog={handleOpeningDialog}
              rowHeader={'Posts'}
            />
          )}

          {/* PROFILE */}
          {[...profileNuggets, ...externalProfileNuggets].length > 0 && (
            <ExpandableNuggetGroup
              nuggetList={[...profileNuggets, ...externalProfileNuggets]}
              handleOpeningDialog={handleOpeningDialog}
              rowHeader={'Profiles'}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

const parentContainerStyle: SxProps<Theme> = {
  width: '100%',
  overflowY: 'scroll',
  height: '100%',
  px: 4,
  '@media (max-width: 600px)': {
    px: 2,
  },
  maxWidth: '1512px',
  margin: '0 auto',
  alignSelf: 'center',
};

const contentContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  width: '100%',
  mb: 3,
  mt: 4,
};

const titleStyle: SxProps<Theme> = {
  backgroundColor: appColors.color.taupe1,
  marginBottom: 2,
  borderRadius: 2,
  paddingX: 5,
  paddingTop: 2,
  paddingBottom: 1,
  fontWeight: 500,
};
